import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDexControls } from './dexControls';
import { formatDexNumber } from '../utils/pokemon';
import { ghostSuffix, rankNames } from '../utils/suggest';

const LIMIT = 8;

function NameTypeahead({
  id,
  label,
  entries,
  query,
  onQueryChange,
  onSelect,
  placeholder = 'Name or number',
  setLabel = 'Set',
  disabled = false,
}) {
  const reactId = useId();
  const fieldId = id || `name-${reactId}`;
  const listId = `${fieldId}-list`;
  const hintId = `${fieldId}-hint`;
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [focused, setFocused] = useState(false);

  const matches = useMemo(() => rankNames(entries, query).slice(0, LIMIT), [entries, query]);
  const active = matches[Math.min(activeIndex, Math.max(0, matches.length - 1))] || null;
  const ghost = open && focused && query ? ghostSuffix(query, active?.name || '') : '';
  const shown = ghost && active ? active.name : query;

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el || document.activeElement !== el || !ghost) return;
    const start = active.name.length - ghost.length;
    el.setSelectionRange(start, active.name.length);
  }, [shown, ghost, active, query]);

  const choose = (entry) => {
    if (!entry) return;
    onQueryChange(entry.name);
    setOpen(false);
    onSelect?.(entry);
  };

  const commit = () => {
    if (active) {
      choose(active);
      return;
    }
    if (String(query || '').trim()) onSelect?.({ id: query, name: query, unmatched: true });
  };

  useDexControls({
    onUp: () => {
      setOpen(true);
      setActiveIndex((index) => Math.max(0, index - 1));
    },
    onDown: () => {
      setOpen(true);
      setActiveIndex((index) => Math.min(matches.length - 1, index + 1));
    },
    onRight: () => {
      if (ghost && active) onQueryChange(active.name);
    },
    onLeft: () => {},
    onA: commit,
    onB: () => {
      if (open) {
        setOpen(false);
        return undefined;
      }
      if (query) {
        onQueryChange('');
        return undefined;
      }
      if (document.querySelector('.filter-drawer')) return false;
      return undefined;
    },
  }, focused);

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.min(matches.length - 1, index + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((index) => Math.max(0, index - 1));
    } else if (event.key === 'ArrowRight' && ghost && active) {
      const el = event.currentTarget;
      if (el.selectionStart <= active.name.length - ghost.length) {
        event.preventDefault();
        onQueryChange(active.name);
      }
    } else if (event.key === 'Tab' && ghost && active && open) {
      onQueryChange(active.name);
      setOpen(false);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      commit();
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    } else if (event.key === 'Backspace' && ghost && active) {
      const el = event.currentTarget;
      const start = active.name.length - ghost.length;
      if (el.selectionStart === start && el.selectionEnd === active.name.length) {
        event.preventDefault();
        onQueryChange(String(query).slice(0, -1));
        setActiveIndex(0);
      }
    }
  };

  const onChange = (event) => {
    const el = event.target;
    const typed = el.value.slice(0, el.selectionStart ?? el.value.length);
    onQueryChange(typed);
    setOpen(true);
    setActiveIndex(0);
  };

  return (
    <div className="suggest">
      <label htmlFor={fieldId}>{label}</label>
      <div className="slot-form-row">
        <input
          ref={inputRef}
          id={fieldId}
          role="combobox"
          className="suggest-input"
          value={shown}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck="false"
          disabled={disabled}
          aria-autocomplete="both"
          aria-expanded={open && matches.length > 0}
          aria-controls={listId}
          aria-activedescendant={open && active ? `${fieldId}-opt-${active.slug || active.id}` : undefined}
          aria-describedby={hintId}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={() => {
            setFocused(true);
            if (query) setOpen(true);
          }}
          onBlur={() => {
            setFocused(false);
            setOpen(false);
          }}
        />
        <button className="btn btn-secondary" type="button" onClick={commit} disabled={disabled}>
          {setLabel}
        </button>
      </div>
      <p id={hintId} className="sr-only">
        Suggestions show a sprite, name, and dex number. Prefix matches come first.
        Tab, Enter, or Right arrow accepts the highlighted name. Up and Down move through the list. Escape closes it.
      </p>
      {open && matches.length > 0 && (
        <ul className="suggest-list" id={listId} role="listbox" aria-label={label}>
          {matches.map((entry, index) => {
            const selected = index === activeIndex;
            return (
              <li key={entry.slug || entry.id} role="presentation">
                <button
                  type="button"
                  role="option"
                  id={`${fieldId}-opt-${entry.slug || entry.id}`}
                  aria-selected={selected}
                  className={selected ? 'suggest-option is-active' : 'suggest-option'}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(entry)}
                >
                  {entry.sprite ? <img src={entry.sprite} alt="" width="36" height="36" /> : <span className="suggest-sprite" aria-hidden="true" />}
                  <span className="suggest-name">{entry.name}</span>
                  <span className="suggest-no">{formatDexNumber(entry.id)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default NameTypeahead;
